import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/layout/Layout';
import WaitlistSection from '../components/WaitlistSection';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ShieldCheck, 
  Globe, 
  Heart, 
  ArrowRight, 
  Star, 
  Users,
  CreditCard,
  MessageSquare,
  CheckCircle,
  Calendar,
  MapPin,
  Sparkles,
  Award,
  Lock,
  Zap
} from 'lucide-react';

const LandingPage = () => {
  const { t, formatCurrency, formatDate, language } = useLanguage();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('guest');

  const handleGetStarted = (role) => {
    if (isAuthenticated) {
      if (user?.role === 'host') {
        navigate('/host/dashboard');
      } else if (user?.role === 'guest') {
        navigate('/weddings');
      } else {
        navigate('/admin');
      }
    } else {
      navigate(`/register?role=${role}`);
    }
  };

  const stats = [
    { value: '500+', label: t('landing.stats.weddings') || 'Hochzeiten', sublabel: t('landing.stats.worldwide') || 'weltweit' },
    { value: '10K+', label: t('landing.stats.guests') || 'Gäste', sublabel: t('landing.stats.matched') || 'vermittelt' },
    { value: '25+', label: t('landing.stats.countries') || 'Länder', sublabel: t('landing.stats.represented') || 'vertreten' },
    { value: '4.9', label: t('landing.stats.rating') || 'Bewertung', sublabel: t('landing.stats.average') || 'Durchschnitt' },
  ];

  const features = [
    {
      icon: ShieldCheck,
      title: t('landing.features.secure.title'),
      description: t('landing.features.secure.desc'),
    },
    {
      icon: Award,
      title: t('landing.features.verified.title'),
      description: t('landing.features.verified.desc'),
    },
    {
      icon: Globe,
      title: t('landing.features.global.title'),
      description: t('landing.features.global.desc'),
    },
    {
      icon: MessageSquare,
      title: t('landing.features.contact.title') || 'Direkter Kontakt',
      description: t('landing.features.contact.desc') || 'Nach der Buchung können Sie direkt mit Ihrem Gastgeber kommunizieren.',
    },
    {
      icon: Lock,
      title: t('landing.features.gdpr.title') || 'DSGVO-Konform',
      description: t('landing.features.gdpr.desc') || 'Ihre Daten sind sicher. Wir halten uns strikt an europäische Datenschutzrichtlinien.',
    },
    {
      icon: Zap,
      title: t('landing.features.instant.title') || 'Sofortige Buchung',
      description: t('landing.features.instant.desc') || 'Schneller Bewerbungsprozess. Innerhalb von 48 Stunden erhalten Sie eine Antwort.',
    },
  ];

  const howItWorks = {
    guest: [
      {
        step: 1,
        title: t('landing.howItWorks.step1.title'),
        description: t('landing.howItWorks.step1.desc'),
      },
      {
        step: 2,
        title: t('landing.howItWorks.step2.title'),
        description: t('landing.howItWorks.step2.desc'),
      },
      {
        step: 3,
        title: t('landing.howItWorks.step3.title'),
        description: t('landing.howItWorks.step3.desc'),
      },
      {
        step: 4,
        title: t('landing.howItWorks.step4.title') || 'Unvergesslich feiern',
        description: t('landing.howItWorks.step4.desc') || 'Erleben Sie eine authentische Hochzeit und schaffen Sie lebenslange Erinnerungen.',
      },
    ],
    host: [
      {
        step: 1,
        title: t('landing.howItWorksHost.step1.title') || 'Hochzeit eintragen',
        description: t('landing.howItWorksHost.step1.desc') || 'Erstellen Sie ein Profil für Ihre Hochzeit mit allen wichtigen Details und Bildern.',
      },
      {
        step: 2,
        title: t('landing.howItWorksHost.step2.title') || 'Bewerbungen prüfen',
        description: t('landing.howItWorksHost.step2.desc') || 'Schauen Sie sich die Profile interessierter Gäste an und entscheiden Sie, wen Sie einladen möchten.',
      },
      {
        step: 3,
        title: t('landing.howItWorksHost.step3.title') || 'Geld erhalten',
        description: t('landing.howItWorksHost.step3.desc') || 'Nach dem Event erhalten Sie 85% der Einnahmen automatisch auf Ihr Konto.',
      },
      {
        step: 4,
        title: t('landing.howItWorksHost.step4.title') || 'Gemeinsam feiern',
        description: t('landing.howItWorksHost.step4.desc') || 'Teilen Sie Ihren besonderen Tag mit Menschen aus aller Welt.',
      },
    ],
  };

  const testimonials = [
    {
      name: 'Sophie & Markus',
      role: 'Gastgeber aus München',
      image: 'https://images.pexels.com/photos/1024993/pexels-photo-1024993.jpeg?auto=compress&cs=tinysrgb&w=150',
      text: 'Wir hatten 4 Gäste aus verschiedenen Ländern bei unserer Hochzeit. Es war unglaublich bereichernd und hat unserem Tag eine ganz besondere Note gegeben.',
      rating: 5,
    },
    {
      name: 'Emma Johnson',
      role: 'Gast aus London',
      image: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150',
      text: 'Die beste Reiseerfahrung meines Lebens! Ich wurde wie ein Familienmitglied behandelt und habe die deutsche Kultur hautnah erlebt.',
      rating: 5,
    },
    {
      name: 'Carlos Martinez',
      role: 'Gast aus Spanien',
      image: 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=150',
      text: 'Ich war skeptisch, aber WEDVAYA hat alles perfekt organisiert. Die Hochzeit in den Alpen war magisch!',
      rating: 5,
    },
  ];

  const [featuredWeddings, setFeaturedWeddings] = useState([]);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const { api } = useAuth();

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await api.get('/weddings/featured');
        setFeaturedWeddings(response.data);
      } catch (error) {
        console.error('Failed to fetch featured weddings:', error);
      } finally {
        setFeaturedLoading(false);
      }
    };
    fetchFeatured();
  }, [api]);

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/3998368/pexels-photo-3998368.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <Badge className="bg-primary/90 text-white mb-6 px-4 py-1.5 text-sm">
              {t('landing.hero.badge') || 'Der #1 Hochzeits-Erlebnis-Marktplatz'}
            </Badge>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {t('landing.hero.title')}
            </h1>
            <p className="text-lg sm:text-xl text-white/90 mb-8 leading-relaxed">
              {t('landing.hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                onClick={() => handleGetStarted('host')}
                data-testid="cta-host"
              >
                <Heart className="mr-2 h-5 w-5" />
                {t('landing.hero.cta_host')}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-foreground rounded-full px-8 py-6 text-lg transition-all"
                onClick={() => handleGetStarted('guest')}
                data-testid="cta-guest"
              >
                <Sparkles className="mr-2 h-5 w-5" />
                {t('landing.hero.cta_guest')}
              </Button>
            </div>
            
            {/* Trust badges */}
            <div className="mt-12 flex flex-wrap gap-6 text-white/80 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span>{t('landing.trust.freeRegistration') || 'Kostenlose Registrierung'}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span>{t('landing.trust.securePayment') || 'Sichere Zahlung'}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span>{t('landing.trust.gdprCompliant') || 'DSGVO-konform'}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            {stats.map((stat, index) => (
              <div key={index} className="animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                <p className="text-4xl md:text-5xl font-bold mb-1">{stat.value}</p>
                <p className="text-white/90 font-medium">{stat.label}</p>
                <p className="text-white/70 text-sm">{stat.sublabel}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-secondary" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">
              {t('landing.features.title')}
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              {t('landing.features.subtitle') || 'Wir machen es einfach und sicher, unvergessliche Hochzeitserlebnisse zu teilen.'}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="bg-white border-none shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
                    <feature.icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="font-serif text-xl font-semibold mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">
              {t('landing.howItWorks.title')}
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8">
              {t('landing.howItWorks.subtitle') || 'In nur wenigen Schritten zu Ihrem unvergesslichen Erlebnis'}
            </p>
            
            {/* Tab Switcher */}
            <div className="inline-flex bg-secondary rounded-full p-1 mb-12">
              <button
                onClick={() => setActiveTab('guest')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  activeTab === 'guest' 
                    ? 'bg-primary text-white shadow-md' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Users className="inline-block h-4 w-4 mr-2" />
                {t('landing.tabs.forGuests') || 'Für Gäste'}
              </button>
              <button
                onClick={() => setActiveTab('host')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  activeTab === 'host' 
                    ? 'bg-primary text-white shadow-md' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Heart className="inline-block h-4 w-4 mr-2" />
                {t('landing.tabs.forHosts') || 'Für Gastgeber'}
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks[activeTab].map((item, index) => (
              <div 
                key={index} 
                className="text-center relative"
              >
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold shadow-lg">
                  {item.step}
                </div>
                {index < 3 && (
                  <div className="hidden lg:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-primary/20" />
                )}
                <h3 className="font-serif text-lg font-semibold mb-3">
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Button
              size="lg"
              className="rounded-full px-8"
              onClick={() => navigate(activeTab === 'guest' ? '/weddings' : '/register?role=host')}
            >
              {activeTab === 'guest' ? t('landing.hero.cta_guest') : (t('landing.cta.becomeHost') || 'Jetzt Gastgeber werden')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">
              {t('landing.testimonials.title') || 'Was unsere Community sagt'}
            </h2>
            <p className="text-muted-foreground text-lg">
              {t('landing.testimonials.subtitle') || 'Echte Erfahrungen von echten Menschen'}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-white border-none shadow-sm">
                <CardContent className="p-8">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-primary text-primary" />
                    ))}
                  </div>
                  <p className="text-muted-foreground mb-6 italic leading-relaxed">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center gap-4">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-semibold">{testimonial.name}</p>
                      <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Weddings */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">
                {t('landing.featured.title') || 'Aktuelle Hochzeiten'}
              </h2>
              <p className="text-muted-foreground text-lg">
                {t('landing.featured.subtitle') || 'Entdecken Sie einzigartige Feiern auf der ganzen Welt'}
              </p>
            </div>
            <Button variant="outline" className="mt-4 md:mt-0 rounded-full" asChild>
              <Link to="/weddings">
                {t('landing.featured.viewAll') || 'Alle anzeigen'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          {featuredLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 py-20">
              {[...Array(3)].map((_, i) => (
                <Card key={i} className="h-48 bg-muted rounded-2xl flex items-center justify-center">
                  <Skeleton className="h-32" />
                </Card>
              ))}
            </div>
          ) : featuredWeddings.length === 0 ? (
            <div className="py-20 text-center text-muted-foreground">
              <p>{t('landing.featured.noFeatured') || 'Keine aktuellen Featured-Weddings gefunden.'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredWeddings.map((wedding) => (
                <Card key={wedding.id} className="overflow-hidden group cursor-pointer" onClick={() => navigate(`/weddings/${wedding.id}`)}>
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={wedding.images?.[0] || "https://images.pexels.com/photos/33485955/pexels-photo-33485955.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"}
                      alt={wedding.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <p className="text-white font-medium text-lg">{wedding.title}</p>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-center text-muted-foreground text-sm mb-2">
                      <MapPin className="h-4 w-4 mr-1.5 text-primary" />
                      {wedding.location}
                    </div>
                    <div className="flex items-center text-muted-foreground text-sm mb-4">
                      <Calendar className="h-4 w-4 mr-1.5 text-primary" />
                      {formatDate(wedding.date)}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-primary">
                        {formatCurrency(wedding.price_per_guest, wedding.currency)}
                      </span>
                      <span className="text-sm text-muted-foreground">pro Person</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-6">
                {t('landing.cta.title')}
              </h2>
              <p className="text-white/90 text-lg mb-8 leading-relaxed">
                {t('landing.cta.description') || 'Ob Sie Ihre Hochzeit mit der Welt teilen möchten oder ein einzigartiges Reiseerlebnis suchen – WEDVAYA macht es möglich. Registrieren Sie sich noch heute kostenlos.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  className="bg-white text-primary hover:bg-white/90 rounded-full px-8 shadow-lg"
                  onClick={() => navigate('/register')}
                >
                  {t('landing.cta.button')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8"
                  onClick={() => navigate('/how-it-works')}
                >
                  {t('landing.cta.learnMore') || 'Mehr erfahren'}
                </Button>
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src="https://images.pexels.com/photos/2253870/pexels-photo-2253870.jpeg?auto=compress&cs=tinysrgb&w=600"
                alt="Glückliches Paar"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Press/Trust Section */}
      <section className="py-12 bg-white border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-muted-foreground mb-8">{t('landing.press.title') || 'Bekannt aus'}</p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-60">
            <div className="text-2xl font-bold text-muted-foreground">ZEIT</div>
            <div className="text-2xl font-bold text-muted-foreground">SPIEGEL</div>
            <div className="text-2xl font-bold text-muted-foreground">SZ</div>
            <div className="text-2xl font-bold text-muted-foreground">FAZ</div>
            <div className="text-2xl font-bold text-muted-foreground">WELT</div>
          </div>
        </div>
      </section>

      <WaitlistSection />
    </Layout>
  );
};

export default LandingPage;
